// ProgressRing: the compact progress mark, for a table cell, a tab key, a heading or a step
// marker, where a bar has no room. The accent arc is how much is filled; the green arc is
// locked (signed off), drawn over the accent so a field locked blank on purpose still reads
// done. `attention` turns the base ring red, breathing, with a "!". The arcs spring to their
// length and the ring flares with accent light once when it closes while someone is looking.
// docs/operations/design-standard.md, "Controls".

import React, { useEffect, useRef, useState } from 'react';
import { useTicker } from './motion';

export interface ProgressRingProps {
  value: number;
  max: number;
  /** Signed off: a green arc over the accent one. */
  locked?: number;
  /** Needs someone now (airspace needing action): the base ring turns red, breathes, and shows "!". */
  attention?: boolean;
  /** Diameter in px: 16 in a tab key, 18 in a table cell, 20 beside a count, 28 as a step marker, 44 as a record's headline. */
  size?: number;
  /** What is complete, ahead of the spoken reading: "Packing: 3 of 8 complete". */
  label?: string;
  /** The figure: `count` is "n of m" beside the ring; `percent` is "n%", inside from 36px and beside below that. */
  display?: 'count' | 'percent';
  /** A word after a count beside the ring: "built" reads "12 of 20 built". */
  unit?: string;
  /** At full the arc turns the done green: for a ring whose closing means done (built, packed), never where green already means locked. */
  success?: boolean;
  /** Hidden from assistive tech, for a ring drawn beside words that already say what it shows. */
  decorative?: boolean;
  className?: string;
}

/** The ring in words, for its own label and for a control that hides the ring and speaks for it. */
export const ringLabel = (value: number, max: number, locked = 0, attention = false, label?: string): string =>
  `${label ? `${label}: ` : ''}${value} of ${max} complete${locked > 0 ? `, ${locked} locked` : ''}${attention ? ', needs action' : ''}`;

const ARC = 'transition-[stroke-dashoffset,stroke,opacity] duration-spring ease-spring motion-reduce:transition-[stroke,opacity] motion-reduce:duration-quick';

const FLARE_MS = 600;
const INSIDE_FROM = 36;

export const ProgressRing: React.FC<ProgressRingProps> = ({
  value, max, locked = 0, attention = false, size = 18, label, display, unit, success, decorative, className = '',
}) => {
  const clamp = (n: number) => (max > 0 ? Math.min(1, Math.max(0, n / max)) : 0);
  const fillPct = clamp(value);
  const lockPct = clamp(locked);
  const stroke = size >= 40 ? 4 : size >= 28 ? 3 : 2;
  const c = size / 2;
  const r = (size - stroke) / 2 - 0.5;
  const circumference = 2 * Math.PI * r;
  const done = max > 0 && (value >= max || locked >= max);
  const pct = max <= 0 ? 0 : value >= max ? 100 : Math.min(99, Math.round((Math.max(0, value) / max) * 100));
  const counted = useTicker(Math.max(0, value));
  const pctShown = useTicker(pct);

  // Only a close seen happening flares; a ring that loads complete is just complete.
  const was = useRef(done);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    if (done && !was.current) setLit(true);
    was.current = done;
  }, [done]);
  useEffect(() => {
    if (!lit) return undefined;
    const t = window.setTimeout(() => setLit(false), FLARE_MS);
    return () => window.clearTimeout(t);
  }, [lit]);

  // At zero the arc is hidden, not unmounted: a round cap on a zero-length dash still draws a dot.
  const arc = (frac: number, tone: string) => (
    <circle
      cx={c} cy={c} r={r}
      fill="none"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeDasharray={circumference}
      strokeDashoffset={circumference - frac * circumference}
      transform={`rotate(-90 ${c} ${c})`}
      className={`${tone} ${ARC}`}
      style={{ opacity: frac > 0 ? 1 : 0 }}
    />
  );

  const u = size / 18;
  const inside = display === 'percent' && size >= INSIDE_FROM && !attention;
  const beside = display === 'count' || (display === 'percent' && size < INSIDE_FROM);
  const said = ringLabel(value, max, locked, attention, label);

  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : said}
      aria-hidden={decorative || undefined}
      className={`inline-flex flex-none items-center gap-1.5 ${className}`}
    >
      <span className="relative inline-flex flex-none">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
          className={`flex-shrink-0 overflow-visible transition-[filter] duration-standard ease-emphasized ${lit ? 'drop-shadow-glow' : ''}`}
        >
          <circle
            cx={c} cy={c} r={r}
            fill="none"
            strokeWidth={stroke}
            className={`transition-colors duration-standard ease-emphasized ${attention ? 'stroke-red-400 motion-safe:animate-pulse' : 'stroke-outline-variant'}`}
          />
          {arc(fillPct, success && value >= max && max > 0 ? 'stroke-emerald-400' : 'stroke-primary')}
          {arc(lockPct, 'stroke-emerald-400')}
          <g className={`fill-red-400 transition-opacity duration-standard ease-emphasized ${attention ? 'opacity-100' : 'opacity-0'}`}>
            <rect x={c - 0.8 * u} y={c - 3.6 * u} width={1.6 * u} height={4.4 * u} rx={0.8 * u} />
            <circle cx={c} cy={c + 2.7 * u} r={0.95 * u} />
          </g>
        </svg>
        {display === 'percent' && size >= INSIDE_FROM && (
          <span
            aria-hidden="true"
            style={{ fontSize: Math.round(size * 0.25) }}
            className={`absolute inset-0 grid place-items-center font-semibold leading-none tabular-nums text-on-surface transition-opacity duration-standard ease-emphasized ${inside ? 'opacity-100' : 'opacity-0'}`}
          >
            {pctShown}%
          </span>
        )}
      </span>
      {beside && (
        <span aria-hidden="true" className="grid flex-none whitespace-nowrap text-xs tabular-nums text-on-surface-variant">
          <span className="invisible col-start-1 row-start-1">
            {display === 'count' ? `${Math.max(max, value)} of ${max}${unit ? ` ${unit}` : ''}` : '100%'}
          </span>
          <span className="col-start-1 row-start-1 text-right">
            {display === 'count' ? <>{counted} of {max}{unit ? ` ${unit}` : ''}</> : `${pctShown}%`}
          </span>
        </span>
      )}
    </span>
  );
};

export default ProgressRing;
